import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import { observer } from 'mobx-react';
import { Node } from 'types';

import {
  KuxMenuItem,
  TypeOfApplicationLink,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '@kaltura-ott/tvpil';
import { featureEnabler, FooterStore, PageStore } from '@kaltura-ott/tvpil-shared';

import { FEATURES, IMAGE_PARAM } from 'consts';
import { useStableReactRouter } from 'context/stableReactRouterContext/stableReactRouterContext';
import { useAppStore, useLoginPageStore, useRootStore } from 'hooks';
import { defaultStore } from 'interface';
import routesPaths from 'routesPaths';

import { applyStyle, applyStyles } from '../../../utils/uiDesignElements';
import Logo from '../Menu/components/Logo/Logo';

import styles from './Footer.module.scss';

interface Props {
  className?: string;
  pageId?: string;
}

// maybe use router location instead of className
const FooterComponent: React.FC<Props> = ({ className, pageId = '' }) => {
  const {
    appStore: {
      cookiePopupDetails,
      cookieStore,
      params: {
        dmsSetting: { partnerId },
      },
    },
  } = useAppStore();
  const rootStore = useRootStore();
  const isGdpr = Boolean(
    featureEnabler.variation(FEATURES.GDPR, false) &&
    !cookieStore?.cookieAccepted &&
    !cookiePopupDetails.loadExternalCookiePopup
  );
  const loginPageStore = useLoginPageStore();
  const { children } = rootStore!;
  const { location } = useStableReactRouter();
  const isLoginPage = location.pathname?.includes(routesPaths.login.path);

  let store = rootStore?.children?.find(
    ({ type, subtype }: defaultStore) =>
      type === UIDAStructureTypeEnum.menu && subtype === UIDAPresentationTypeEnum.footer,
  ) as FooterStore;
  if (pageId) {
    const pageStore = children?.find(
      ({ type, referenceId }: defaultStore) => type === UIDAStructureTypeEnum.page && pageId === referenceId,
    ) as PageStore;
    const pageMenu = pageStore?.children?.find(
      ({ type, subtype }: defaultStore) =>
        type === UIDAStructureTypeEnum.menu && subtype === UIDAPresentationTypeEnum.footer,
    ) as FooterStore;
    if (pageMenu) {
      store = pageMenu;
    }
  }

  const imageParam = (index: number) =>
    partnerId === 3201 && index === 0 ? '/height/24/width/57' : '/height/34/width/34';

  const handleRef = useCallback((node: Node) => {
    const footerStore = rootStore?.children?.find(
      ({ type, subtype }: defaultStore) =>
        type === UIDAStructureTypeEnum.menu && subtype === UIDAPresentationTypeEnum.footer,
    ) as FooterStore;
    const uiDesignElements = footerStore?.uiDesignElements;
    const { image } = loginPageStore!;

    if (uiDesignElements && node) {
      applyStyles(node, uiDesignElements);
    }

    if (node && isLoginPage && image) {
      applyStyle(node, '--background', 'linear-gradient(180deg, rgba(36, 37, 64, 0) 0%, rgba(36, 37, 64, 0.5) 100%)');
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!store?.isReady && store) {
      store.init();
    }
  }, [store]);

  if (!store) {
    return (
      <div className={styles.message}>
        <FormattedMessage id='Footer.messageWrong' />
      </div>
    );
  }

  if (!store.isReady) {
    return null;
  }

  const logo = store.items.find(
    (item: any) =>
      item?.typeOfAppLink === UIDAStructureTypeEnum.page && item?.image && item?.name.toLocaleLowerCase() === 'home',
  );

  return (
    <footer ref={handleRef} className={cn(styles.footer, className, {[styles.hidden]: isGdpr})}>
      <div className={styles.follow}>
        {logo && <Logo description={logo?.name} className={styles.logo} image={`${logo.image}${IMAGE_PARAM}`} />}
        <ul className={cn({ [styles.o2]: partnerId === 3201 })}>
          {store.items
            .filter(
              (item: any) =>
                ![TypeOfApplicationLink.external, TypeOfApplicationLink.page].includes(
                  item?.typeOfAppLink as TypeOfApplicationLink, // TODO: add this type to tvpil
                ) && !!item?.inAppLink,
            )
            .map((item: KuxMenuItem, index) => (
              <li key={`default-${item.id}`}>
                <a
                  aria-label={item.name}
                  href={item.inAppLink}
                  rel='noopener noreferrer'
                  style={{
                    backgroundImage: `url('${item.image}${imageParam(index)}')`,
                  }}
                  target='_blank'
                >
                  {item.name}
                </a>
              </li>
            ))}
        </ul>
      </div>

      <div className={styles.externalMenu}>
        {store.items
          .filter((item: any) => item?.typeOfAppLink === TypeOfApplicationLink.external)
          .map((item: KuxMenuItem) => (
            <a key={`external-${item.id}`} aria-label={item?.name} href={item.inAppLink} rel='noopener noreferrer' target='_blank'>
              {item?.name}
            </a>
          ))}
      </div>

      <p className={styles.copy}>
        <FormattedMessage id='Footer.companyInfo' />
      </p>
    </footer>
  );
};

export const Footer = observer(FooterComponent);
